import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { useAppDispatch } from "app/hooks";
import { MARKETING_AUTOMATION_FEATURE } from "constants/feature-flags";
import { VIEW_OWN_SCHEDULED_ONLY } from "constants/permissions";
import useAppointments from "features/scheduling/hooks/use-appointments.hook";
import { openForm } from "features/scheduling/scheduler-slice";
import useStaffTasksV2 from "features/staff-tasks-v2/hooks/use-staff-tasks-v2.hook";
import useEmail from "hooks/use-email.hook";
import useNewTouchpoint from "hooks/use-new-touchpoint";
import useUserRoles from "hooks/use-user-roles.hook";
import type { Patient } from "services/types/patients.type";

interface PatientQuickActionsProps {
  patient: Patient;
}

export function PatientQuickActions({ patient }: PatientQuickActionsProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const flags = useFlags();
  const { userPermissions, currentUser: staffMember } = useUserRoles();
  const viewOwnScheduleOnly = userPermissions[VIEW_OWN_SCHEDULED_ONLY];
  const showTouchpoints = flags[MARKETING_AUTOMATION_FEATURE];

  const { setupAppointmentModal, appointmentFormId } = useAppointments();
  const { handleOpen: handleOpenEmailTouchpoint } = useNewTouchpoint();
  const { handleOpen: handleOpenEmail } = useEmail();
  const { handleOpenTaskDrawer } = useStaffTasksV2();

  const openTouchpointForm = useCallback(() => {
    handleOpenEmailTouchpoint(patient);
  }, [handleOpenEmailTouchpoint, patient]);

  const openEmailForm = useCallback(() => {
    handleOpenEmail({ patient });
  }, [handleOpenEmail, patient]);

  const openCreateTaskDrawer = useCallback(() => {
    handleOpenTaskDrawer("create", patient.id);
  }, [handleOpenTaskDrawer, patient.id]);

  const isOnMessagesPage =
    pathname.startsWith("/inbox/") ||
    pathname.endsWith("/touchpoints/messages");

  const actions = [
    {
      label: "New Appointment",
      icon: <CalendarMonthOutlinedIcon fontSize="small" />,
      onClick: () => {
        setupAppointmentModal(
          {
            patient,
            staffMember: viewOwnScheduleOnly ? staffMember : undefined,
            staffMemberId: viewOwnScheduleOnly ? staffMember?.id : undefined
          },
          "add",
          () => navigate(`/patients/${patient.id}/appointments`)
        );
        dispatch(openForm(appointmentFormId));
      }
    },
    {
      label: "New Invoice",
      icon: <NoteAddOutlinedIcon fontSize="small" />,
      onClick: () => navigate(`/patients/${patient.id}/billing/new`)
    },
    {
      label: "Create Task",
      icon: <TaskAltIcon fontSize="small" />,
      onClick: openCreateTaskDrawer
    },
    ...(!isOnMessagesPage
      ? [
          {
            label: "Send Text",
            icon: <SmsOutlinedIcon fontSize="small" />,
            onClick: () =>
              navigate(`/patients/${patient.id}/touchpoints/messages`)
          }
        ]
      : []),
    {
      label: "Send Email",
      icon: <EmailOutlinedIcon fontSize="small" />,
      onClick: openEmailForm
    },
    ...(showTouchpoints
      ? [
          {
            label: "New Touchpoint",
            icon: <ChatBubbleOutlineIcon fontSize="small" />,
            onClick: openTouchpointForm
          }
        ]
      : [])
  ];

  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      gap={1}
      justifyContent="center"
      my={1}
    >
      {actions.map(({ label, icon, onClick }) => (
        <Stack
          key={label}
          alignItems="center"
          width="104px"
          flexWrap="wrap"
          onClick={onClick}
          sx={{
            cursor: "pointer"
          }}
        >
          <IconButton size="small" sx={{ p: 0.75 }}>
            {icon}
          </IconButton>
          <Typography variant="caption" sx={{ textAlign: "center" }}>
            {label}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}
